import React, { useEffect, useState, useCallback } from 'react';

/**
 * Guest Receipts (docs/DEV_PMS_OPERATIONS_ENHANCEMENT.md Module 4) — Receipt
 * History / Reprint. Reads GET /receipts, which unions pms_reservation_advance
 * and pms_folio_ledger filtered to receipt_no IS NOT NULL — no separate
 * receipts table, so this is never a second source of truth for the money
 * movement itself.
 */
function PmsReceiptHistory({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [correctRow, setCorrectRow] = useState(null);
  const [correctForm, setCorrectForm] = useState({ new_mode: '', reference_no: '', reason: '' });
  const [correctBusy, setCorrectBusy] = useState(false);
  const [correctError, setCorrectError] = useState('');

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) { setRows([]); return; }
    setLoading(true); setError('');
    try {
      const q = new URLSearchParams();
      if (fromDate) q.set('from_date', fromDate);
      if (toDate) q.set('to_date', toDate);
      const res = await fetch(`/api/v1/pms/receipts?${q.toString()}`, { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load receipts');
      setRows(Array.isArray(data.receipts) ? data.receipts : []);
    } catch (err) {
      setError(err?.message || 'Failed to load receipts');
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, fromDate, toDate, headers]);

  useEffect(() => { load(); }, [load]);

  const openCorrect = (row) => {
    setCorrectRow(row);
    setCorrectForm({ new_mode: row.payment_mode || '', reference_no: row.reference_no || '', reason: '' });
    setCorrectError('');
  };

  const submitCorrection = async () => {
    if (!correctRow) return;
    if (!correctForm.new_mode) return setCorrectError('Select the correct payment mode');
    if (!correctForm.reason.trim()) return setCorrectError('Please enter a reason for the correction');
    setCorrectBusy(true); setCorrectError('');
    try {
      const res = await fetch(`/api/v1/pms/reservations/${correctRow.reservation_id}/advances/${correctRow.advance_id}`, {
        method: 'PATCH', headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_mode: correctForm.new_mode, reference_no: correctForm.reference_no.trim(), reason: correctForm.reason.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to correct payment mode');
      setCorrectRow(null);
      await load();
      if (data.warning) window.alert(data.warning);
    } catch (err) {
      setCorrectError(err?.message || 'Failed to correct payment mode');
    } finally {
      setCorrectBusy(false);
    }
  };

  // Same rupees-to-words helper used by the Invoice print (PmsCommandCenter.js,
  // PmsInvoiceRegister.js) — duplicated locally since it isn't in a shared
  // utils module; kept byte-for-byte identical so wording matches everywhere.
  const numberToWordsLocal = (amount) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const convert = (n) => {
      if (n === 0) return '';
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
      if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
      if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
      return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
    };
    const num = Math.abs(Number(amount) || 0);
    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);
    let words = rupees === 0 ? 'Zero' : convert(rupees);
    words = 'Rupees ' + words;
    if (paise > 0) words += ' and ' + convert(paise) + ' Paise';
    return words + ' Only';
  };

  // Full letterhead-style print, matching the Invoice print's look (bordered
  // A4 box, hotel letterhead with GSTIN, title bar with doc no/date) instead
  // of the old plain monospace slip — same visual language as
  // PmsCommandCenter.js's buildInvoiceHtmlLocal so a receipt and an invoice
  // from the same property look like they belong to the same document set.
  // Printed via a separate popup window (same pattern every other print in this
  // codebase uses — PmsInvoiceRegister.js's handlePrint, Command Center's
  // checkout invoice) rather than window.print() on the current page: printing
  // the current page can only hide/show elements INSIDE this component via
  // @media print, it can never hide the surrounding app header/nav that live
  // in parent components, so the whole app chrome printed along with the slip.
  const printReceipt = async (row) => {
    let hotel = {};
    try {
      const res = await fetch('/api/v1/pms/billing-profiles?payer_type=SELF', { headers: headers() });
      const data = await res.json().catch(() => []);
      if (res.ok && Array.isArray(data) && data.length) hotel = data[0];
    } catch (_) { /* fall back to plain property name below */ }

    const hotelName = hotel.billing_company_name || selectedProperty?.name || 'Hotel';
    const hotelAddr = [hotel.address_line1, hotel.address_line2, hotel.city, hotel.state, hotel.pin_code, hotel.country].filter(Boolean).join(', ');
    const hotelGstin = hotel.gstin_number || '';
    const contact = [hotel.phone ? `Tel: ${hotel.phone}` : '', hotel.email ? `Email: ${hotel.email}` : ''].filter(Boolean).join(' | ');
    const logoSrc = hotel.logo_url ? (hotel.logo_url.startsWith('http') ? hotel.logo_url : window.location.origin + hotel.logo_url) : '';

    const txnType = String(row.txn_type || 'PAYMENT').toUpperCase();
    const docTitle = { PAYMENT: 'PAYMENT RECEIPT', ADVANCE: 'ADVANCE RECEIPT', REFUND: 'REFUND RECEIPT' }[txnType] || 'RECEIPT';
    const toMoney = (v) => Number(v || 0).toFixed(2);
    const fmtDateTime = (dt) => dt ? new Date(dt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

    const html = `<html><head><title>Receipt ${row.receipt_no}</title>
<style>
  @page { size: A4; margin: 10mm 12mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, Helvetica, sans-serif; font-size: 11px; color: #1a1a1a; padding: 8px; }
  table { border-collapse: collapse; width: 100%; }
  .lbl { padding: 3px 0; font-weight: 600; color: #333; width: 140px; font-size: 10.5px; }
  .val { padding: 3px 0; font-size: 10.5px; }
  @media print { body { padding: 0; } }
</style>
</head><body>
<div style="max-width:520px;margin:0 auto;border:2px solid #333;">
  <div style="padding:12px 18px;text-align:center;border-bottom:2px solid #333;">
    ${logoSrc ? `<img src="${logoSrc}" alt="Logo" style="height:50px;margin-bottom:6px;" onerror="this.style.display='none'"><br>` : ''}
    <div style="font-size:17px;font-weight:700;letter-spacing:1.5px;">${hotelName}</div>
    ${hotelAddr ? `<div style="font-size:10px;margin-top:3px;color:#444;">${hotelAddr}</div>` : ''}
    ${contact ? `<div style="font-size:9.5px;color:#555;margin-top:2px;">${contact}</div>` : ''}
    ${hotelGstin ? `<div style="font-size:10px;margin-top:3px;font-weight:600;">GSTIN: ${hotelGstin}</div>` : ''}
  </div>
  <div style="display:flex;justify-content:space-between;align-items:center;padding:6px 18px;background:#f0f0f0;border-bottom:2px solid #333;font-weight:700;">
    <div style="font-size:14px;letter-spacing:3px;">${docTitle}</div>
    <div style="text-align:right;font-size:10.5px;"><div>Receipt No: <span style="font-size:12px;">${row.receipt_no}</span></div><div style="font-weight:400;margin-top:1px;">Date: ${fmtDateTime(row.txn_date)}</div></div>
  </div>
  <div style="padding:10px 18px;border-bottom:1.5px solid #333;">
    <table>
      <tr><td class="lbl">Guest Name</td><td class="val" style="font-weight:600;">${row.primary_guest_name || '-'}</td></tr>
      ${row.mobile_number ? `<tr><td class="lbl">Mobile</td><td class="val">${row.mobile_number}</td></tr>` : ''}
      ${row.reservation_no ? `<tr><td class="lbl">Reservation No</td><td class="val">${row.reservation_no}</td></tr>` : ''}
      ${row.room_number ? `<tr><td class="lbl">Room No</td><td class="val">${row.room_number}</td></tr>` : ''}
      <tr><td class="lbl">Payment Mode</td><td class="val" style="font-weight:600;">${row.payment_mode || '-'}</td></tr>
      ${row.reference_no ? `<tr><td class="lbl">Reference No</td><td class="val">${row.reference_no}</td></tr>` : ''}
      ${row.remarks ? `<tr><td class="lbl">Remarks</td><td class="val">${row.remarks}</td></tr>` : ''}
    </table>
  </div>
  <div style="padding:10px 18px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #ddd;">
    <div style="font-size:12px;font-weight:700;">${txnType === 'REFUND' ? 'Amount Refunded' : 'Amount Received'}</div>
    <div style="font-size:17px;font-weight:700;">₹${toMoney(row.amount)}</div>
  </div>
  <div style="padding:6px 18px;font-size:9.5px;color:#555;border-bottom:1.5px solid #333;"><b>Amount in words:</b> ${numberToWordsLocal(row.amount)}</div>
  <div style="padding:6px 18px;font-size:9px;color:#666;line-height:1.4;">This is a computer-generated receipt and does not require a signature.</div>
  <div style="text-align:center;padding:6px 18px 12px;border-top:1px solid #eee;font-size:10px;color:#555;font-style:italic;">Thank you for staying with us. We look forward to welcoming you again!</div>
</div>
</body></html>`;
    const popup = window.open('', '_blank', 'width=560,height=760');
    if (!popup) return;
    popup.document.open();
    popup.document.write(html);
    popup.document.close();
    popup.focus();
    popup.print();
  };

  const tdStyle = { padding: '0.4rem 0.5rem', fontSize: '0.83rem' };
  const thStyle = { padding: '0.45rem 0.5rem', textAlign: 'left', fontSize: '0.82rem' };

  return (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Receipt History</h3>
      {error ? <div className="error-banner">{error}</div> : null}

      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-end', marginBottom: '0.7rem', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
          From
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.82rem', fontWeight: 600 }}>
          To
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
              <th style={thStyle}>Receipt No</th>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Type</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
              <th style={thStyle}>Mode</th>
              <th style={thStyle}>Guest</th>
              <th style={thStyle}>Room</th>
              <th style={thStyle}>Reservation</th>
              <th style={thStyle}>Print</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.receipt_no} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{r.receipt_no}</td>
                <td style={tdStyle}>{new Date(r.txn_date).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                <td style={tdStyle}>{r.txn_type}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>₹{Number(r.amount || 0).toFixed(2)}</td>
                <td style={tdStyle}>{r.payment_mode || '-'}</td>
                <td style={tdStyle}>{r.primary_guest_name || '-'}</td>
                <td style={tdStyle}>{r.room_number || '-'}</td>
                <td style={tdStyle}>{r.reservation_no || '-'}</td>
                <td style={tdStyle}>
                  <button onClick={() => printReceipt(r)}>Reprint</button>
                  {r.source === 'RESERVATION' && r.advance_id ? (
                    <button onClick={() => openCorrect(r)} style={{ marginLeft: '0.4rem' }}>✎ Correct</button>
                  ) : null}
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr><td colSpan={9} style={{ padding: '0.7rem', color: 'var(--theme-text-secondary)' }}>No receipts found for this range.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {correctRow && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ width: 'min(480px,94vw)', background: 'var(--theme-surface, #fff)', borderRadius: 10, padding: '1.3rem', position: 'relative' }}>
            <button onClick={() => setCorrectRow(null)} style={{ position: 'absolute', top: '0.5rem', right: '0.6rem', border: '1px solid #cbd5e0', background: '#fff', borderRadius: 4, padding: '0.05rem 0.4rem', fontSize: '1rem', cursor: 'pointer' }}>×</button>
            <h3 style={{ marginTop: 0, marginBottom: '0.3rem', color: 'var(--theme-primary-dark)' }}>Correct Payment Mode</h3>
            <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.8rem' }}>
              Receipt <b>{correctRow.receipt_no}</b> — {correctRow.txn_type} ₹{Number(correctRow.amount || 0).toFixed(2)} ({correctRow.primary_guest_name || '-'})
            </div>
            {correctError && <div className="error-banner" style={{ marginBottom: '0.6rem' }}>{correctError}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>New Mode
                <select value={correctForm.new_mode} onChange={(e) => setCorrectForm({ ...correctForm, new_mode: e.target.value })} style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.85rem' }}>
                  {['CASH', 'CARD', 'UPI', 'BANK_TRANSFER', 'CHEQUE'].map(m => <option key={m}>{m}</option>)}
                </select>
              </label>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>Reference No{['CARD', 'UPI', 'BANK_TRANSFER'].includes(correctForm.new_mode) ? ' *' : ''}
                <input value={correctForm.reference_no} onChange={(e) => setCorrectForm({ ...correctForm, reference_no: e.target.value })} placeholder="e.g. UPI transaction ID" style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.85rem' }} />
              </label>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>Reason *
                <input value={correctForm.reason} onChange={(e) => setCorrectForm({ ...correctForm, reason: e.target.value })} placeholder="e.g. Payment confirmed via UPI on follow-up" style={{ padding: '0.45rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.85rem' }} />
              </label>
              <button onClick={submitCorrection} disabled={correctBusy} style={{ padding: '0.45rem 1rem', border: 'none', background: 'var(--theme-primary)', color: '#fff', borderRadius: 6, cursor: correctBusy ? 'default' : 'pointer', fontSize: '0.83rem', fontWeight: 600, opacity: correctBusy ? 0.6 : 1 }}>{correctBusy ? 'Saving…' : 'Correct Mode'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PmsReceiptHistory;
